import {
  ActionError,
  AttentionBlock,
  Button,
  ConfirmDialog,
  CopyField,
  Field,
  Kpi,
  KpiGrid,
  Surface,
  Textarea,
  formatDate,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { ActionCard } from '@/features/users/action-card'
import { MSG_JUSTIFICATION, isJustification } from '@/features/users/labels'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useRegenerateKey, useRevokeIntegration, type Integration } from './api'
import { IntegrationKey } from './integration-key'
import { curlTest, DOCS_URL, REGENERATE_ANCHOR, REVOKE_ANCHOR } from './labels'

/**
 * Visão geral do app: os números das últimas 24 horas, o "Comece por aqui" e, depois do último bloco, os dois
 * cartões que mexem no acesso: regenerar a chave (com justificativa, RN-ADM-41) e revogar o app (RN-ADM-42).
 */

const stepClass = 'font-body text-body text-ink leading-[1.7] font-light'
const linkClass = 'text-ink border-hairline-heavy hover:border-ink border-b'

function timeOf(iso: string) {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function OverviewTab({ integration }: { integration: Integration }) {
  const location = useLocation()
  const navigate = useNavigate()
  const regenerate = useRegenerateKey(integration.id)
  const revoke = useRevokeIntegration(integration.id)
  const isActive = integration.status === 'active'

  const [newKey, setNewKey] = useState<string | null>(null)
  const [failure, setFailure] = useState<{ title: string; description: string } | null>(null)
  const [regenerating, setRegenerating] = useState(location.hash === `#${REGENERATE_ANCHOR}`)
  const [justification, setJustification] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false)
  const [confirmingRevoke, setConfirmingRevoke] = useState(
    location.hash === `#${REVOKE_ANCHOR}` && isActive,
  )
  const [revokeReason, setRevokeReason] = useState('')

  function askRegenerate() {
    setAttempted(true)
    if (isJustification(justification)) setConfirmingRegenerate(true)
  }

  function confirmRegenerate() {
    regenerate.mutate(justification.trim(), {
      onSuccess: (result) => {
        setConfirmingRegenerate(false)
        setRegenerating(false)
        setJustification('')
        setAttempted(false)
        setNewKey(result.key)
        toast(
          result.emailsSent > 0
            ? 'Chave nova gerada; as gestoras receberam o aviso por e-mail'
            : 'Chave nova gerada',
        )
      },
      onError: (error) => {
        setConfirmingRegenerate(false)
        setFailure({
          title: 'Não foi possível regenerar a chave',
          description: errorMessage(error),
        })
      },
    })
  }

  function confirmRevoke() {
    const reason = revokeReason.trim()
    revoke.mutate(reason.length > 0 ? reason : null, {
      onSuccess: () => {
        toast(`${integration.name} revogado; a chave deixou de valer agora`)
        navigate(routes.admin.integrations)
      },
      onError: (error) => {
        setConfirmingRevoke(false)
        setFailure({ title: 'Não foi possível revogar o app', description: errorMessage(error) })
      },
    })
  }

  const revokeReasonInvalid = revokeReason.trim().length > 0 && !isJustification(revokeReason)

  return (
    <div className="flex flex-col gap-6">
      {failure ? (
        <ActionError
          title={failure.title}
          description={failure.description}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      {newKey !== null ? (
        <IntegrationKey appKey={newKey} previousKeyValidUntil={integration.previousKeyValidUntil} />
      ) : integration.previousKeyValidUntil ? (
        <AttentionBlock variant="lilac" title="A chave anterior ainda vale por alguns minutos">
          Até {timeOf(integration.previousKeyValidUntil)}, a chave anterior continua aceita, para o
          sistema trocar sem queda. Depois, só a nova funciona.
        </AttentionBlock>
      ) : null}

      <KpiGrid aria-label="Últimas 24 horas">
        <Kpi
          label="Chamadas"
          value={integration.usage.requests24h}
          note="nas últimas 24 horas, API e MCP"
        />
        <Kpi label="Com erro" value={integration.usage.errors24h} note="respostas 4xx e 5xx" />
        <Kpi
          label="Entregas"
          value={integration.deliveries.succeeded24h}
          note="webhooks entregues em 24 horas"
        />
        <Kpi
          label="Com falha"
          value={integration.deliveries.failed}
          note={
            integration.deliveries.failed > 0
              ? 'esperando reenvio, na aba Entregas'
              : 'nenhuma entrega perdida'
          }
          tone={integration.deliveries.failed > 0 ? 'critical' : 'default'}
        />
      </KpiGrid>

      {isActive ? (
        <Surface role="region" className="max-desk:px-4" aria-labelledby="start-title">
          <h2 id="start-title" className="font-display text-block text-ink font-light">
            Comece por aqui
          </h2>
          <ol className="mt-4 flex flex-col gap-5">
            <li className="flex flex-col gap-3">
              <span className={stepClass}>
                <span className="font-display font-normal">1.</span> Teste a chave. A resposta traz
                o app, a clínica e as permissões.
              </span>
              <CopyField
                multiline
                value={curlTest('wx_SUA_CHAVE')}
                copyLabel="Copiar comando"
                copyAriaLabel="Copiar o comando de teste"
                note={`A chave começa com wx_${integration.keyPrefix}. Ela apareceu uma vez, ao criar ou regenerar.`}
              />
            </li>
            <li className={stepClass}>
              <span className="font-display font-normal">2.</span> Leia como cadastrar a paciente,
              mandar a anamnese e gerar o contrato em{' '}
              <a href={DOCS_URL} target="_blank" rel="noreferrer" className={linkClass}>
                docs.westhetics.io
              </a>
              .
            </li>
            <li className={stepClass}>
              <span className="font-display font-normal">3.</span> Para saber na hora quando a
              anamnese é respondida ou o contrato é assinado,{' '}
              <Link
                to={routes.admin.integrationTab(integration.id, 'webhooks')}
                className={linkClass}
              >
                crie um webhook
              </Link>
              .
            </li>
          </ol>
        </Surface>
      ) : null}

      {isActive ? (
        <>
          <ActionCard
            id={REGENERATE_ANCHOR}
            title="Regenerar a chave"
            description="Gera uma chave nova, mostrada uma vez. A atual continua aceita por 15 minutos, para o sistema trocar sem queda; depois, só a nova funciona. As gestoras recebem o aviso por e-mail."
            action={
              regenerating ? null : (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFailure(null)
                    setRegenerating(true)
                  }}
                >
                  Regenerar a chave
                </Button>
              )
            }
          >
            {regenerating ? (
              <>
                <Field
                  label="Justificativa"
                  meta={`${justification.trim().length}/500`}
                  error={
                    attempted && !isJustification(justification) ? MSG_JUSTIFICATION : undefined
                  }
                  hint="Fica na trilha da clínica, com o seu nome e a data."
                >
                  <Textarea
                    autoFocus
                    value={justification}
                    maxLength={500}
                    onChange={(event) => setJustification(event.target.value)}
                  />
                </Field>
                <div className="flex flex-wrap items-center gap-4">
                  <Button variant="outline" onClick={askRegenerate}>
                    Regenerar a chave
                  </Button>
                  <Button variant="text" onClick={() => setRegenerating(false)}>
                    Desistir
                  </Button>
                </div>
              </>
            ) : null}
          </ActionCard>

          <ActionCard
            id={REVOKE_ANCHOR}
            title="Revogar o app"
            description="A chave deixa de valer na hora, em toda chamada da API e do MCP, e os webhooks param. Não tem volta: para conectar de novo, crie outro app. O que o app fez continua na trilha."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  setFailure(null)
                  setConfirmingRevoke(true)
                }}
              >
                Revogar o app
              </Button>
            }
          />
        </>
      ) : (
        <ActionCard
          title="App revogado"
          description={`Revogado ${
            integration.revokedAt ? `em ${formatDate(new Date(integration.revokedAt))} ` : ''
          }por ${titleCase(integration.revokedBy.name)}. A chave não vale mais e os webhooks pararam; o que o app fez continua na trilha.`}
          action={null}
        />
      )}

      <ConfirmDialog
        open={confirmingRegenerate}
        onOpenChange={setConfirmingRegenerate}
        title={`Regenerar a chave de "${integration.name}"?`}
        effect="A chave atual continua aceita por 15 minutos e depois para de funcionar. Troque no sistema que usa o app antes disso. As permissões, os webhooks e o histórico continuam como estão."
        confirmLabel="Regenerar a chave"
        loading={regenerate.isPending}
        loadingLabel="Regenerando…"
        onConfirm={confirmRegenerate}
      />

      <ConfirmDialog
        open={confirmingRevoke}
        onOpenChange={setConfirmingRevoke}
        title={`Revogar "${integration.name}"?`}
        effect="Toda chamada com esta chave passa a falhar agora, e as entregas de webhook na fila não saem mais. Nada do que o app fez é apagado: as pacientes, os documentos e a trilha continuam."
        alternative={{
          description: 'Se a chave vazou, regenere: o app continua, com outra chave.',
          label: 'Regenerar a chave',
          onSelect: () => {
            setConfirmingRevoke(false)
            setRegenerating(true)
          },
        }}
        confirmLabel="Revogar o app"
        loading={revoke.isPending}
        loadingLabel="Revogando…"
        onConfirm={() => {
          if (!revokeReasonInvalid) confirmRevoke()
        }}
      >
        <Field
          label="Motivo"
          optional
          error={revokeReasonInvalid ? MSG_JUSTIFICATION : undefined}
          hint="Fica na trilha da clínica."
        >
          <Textarea
            rows={2}
            value={revokeReason}
            maxLength={500}
            onChange={(event) => setRevokeReason(event.target.value)}
          />
        </Field>
      </ConfirmDialog>
    </div>
  )
}
